import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';

export interface OrderStatusData {
  pending: number;
  processing: number;
  /** Đơn đã chuẩn bị xong, chờ khách nhận tại quầy. */
  ready: number;
  completed: number;
  total: number;
}

interface OrderStatusGridProps {
  data: OrderStatusData;
  isLoading?: boolean;
  className?: string;
}

// ---- Icons ----

function PendingIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="3" width="14" height="18" rx="2" stroke="#D08700" strokeWidth="1.6" fill="none"/>
      <path d="M9 7h6M9 11h6M9 15h4" stroke="#D08700" strokeWidth="1.4" strokeLinecap="round"/>
      <circle cx="17" cy="17" r="4" fill="#FEFCE8" stroke="#D08700" strokeWidth="1.4"/>
      <path d="M17 15v2l1 1" stroke="#D08700" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

function ProcessingIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M6 2h9l4 4v14a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" stroke="#0288D1" strokeWidth="1.6" fill="none"/>
      <path d="M14 2v5h5" stroke="#0288D1" strokeWidth="1.4"/>
      <path d="M9 12l2 2 4-4" stroke="#0288D1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ReadyIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M4 8h16l-1.2 11a2 2 0 01-2 1.8H7.2a2 2 0 01-2-1.8L4 8z" stroke="#AD46FF" strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
      <path d="M8.5 8V6a3.5 3.5 0 017 0v2" stroke="#AD46FF" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M9.5 13.5l2 2 3.5-3.5" stroke="#AD46FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function CompletedIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M6 2h9l4 4v14a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" stroke="#1FBE63" strokeWidth="1.6" fill="none"/>
      <path d="M14 2v5h5" stroke="#1FBE63" strokeWidth="1.4"/>
      <path d="M8 13l2.5 2.5L16 10" stroke="#1FBE63" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ---- Card ----

interface StatusCardProps {
  count: number;
  label: string;
  percentage: number;
  bg: string;
  border: string;
  countColor: string;
  labelColor: string;
  pctColor: string;
  barBg: string;
  barFill: string;
  icon: React.ReactNode;
  iconBg: string;
}

function StatusCard({
  count, label, percentage,
  bg, border, countColor, labelColor, pctColor, barBg, barFill,
  icon, iconBg,
}: StatusCardProps) {
  return (
    <div
      className={cn('flex flex-col h-full rounded-xl border', bg, border)}
      style={{ padding: '10px', gap: '6px', borderRadius: '12px' }}
    >
      {/* Icon + Count row */}
      <div className="flex items-center justify-between">
        <div className={cn('flex h-8 w-8 items-center justify-center rounded-xl', iconBg)}>
          {icon}
        </div>
        <span className={cn('text-2xl font-bold leading-none', countColor)}>{count}</span>
      </div>
      {/* Label + % row */}
      <div className="flex items-center justify-between">
        <span className={cn('text-sm font-semibold', labelColor)}>{label}</span>
        <span className={cn('text-xs font-medium', pctColor)}>{percentage}%</span>
      </div>
      {/* Progress bar */}
      <div className={cn('h-1.5 w-full overflow-hidden rounded-full', barBg)}>
        <div
          className={cn('h-full rounded-full', barFill)}
          style={{ width: `${Math.min(Math.max(percentage, 2), 100)}%` }}
        />
      </div>
    </div>
  );
}

// ---- Main ----

export function OrderStatusGrid({ data, isLoading = false, className }: OrderStatusGridProps) {
  const { t } = useI18n();

  if (isLoading) {
    return (
      <div className={cn('rounded-xl bg-white p-6 shadow-sm', className)}>
        <div className="mb-4 h-6 w-40 animate-pulse rounded bg-slate-200" />
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-[120px] animate-pulse rounded-xl bg-slate-200" />
          ))}
        </div>
      </div>
    );
  }

  const total = Math.max(
    data.total || data.pending + data.processing + data.ready + data.completed,
    1
  );
  const pct = (v: number) => Math.round((v / total) * 100);

  const cards: StatusCardProps[] = [
    {
      count: data.pending,
      label: t('pending'),
      percentage: pct(data.pending),
      bg: 'bg-[#FEFCE8]',
      border: 'border-[#FFF085]',
      countColor: 'text-[#A65F00]',
      labelColor: 'text-[#A65F00]',
      pctColor: 'text-[#D08700]',
      barBg: 'bg-[#FFF5AB]',
      barFill: 'bg-[#F0B100]',
      icon: <PendingIcon />,
      iconBg: 'bg-white/70',
    },
    {
      count: data.processing,
      label: t('processing'),
      percentage: pct(data.processing),
      bg: 'bg-[#E5F6FD]',
      border: 'border-[#D0E5FF]',
      countColor: 'text-[#0288D1]',
      labelColor: 'text-[#0288D1]',
      pctColor: 'text-[#0288D1]',
      barBg: 'bg-[#BEDBFF]',
      barFill: 'bg-[#0288D1]',
      icon: <ProcessingIcon />,
      iconBg: 'bg-white/70',
    },
    {
      count: data.ready,
      label: t('ready'),
      percentage: pct(data.ready),
      bg: 'bg-[#FAF5FF]',
      border: 'border-[#EDDEFE]',
      countColor: 'text-[#AD46FF]',
      labelColor: 'text-[#AD46FF]',
      pctColor: 'text-[#AD46FF]',
      barBg: 'bg-[#E9D7FB]',
      barFill: 'bg-[#AD46FF]',
      icon: <ReadyIcon />,
      iconBg: 'bg-white/70',
    },
    {
      count: data.completed,
      label: t('completed'),
      percentage: pct(data.completed),
      bg: 'bg-[#F0FDF4]',
      border: 'border-[#C0F9D4]',
      countColor: 'text-[#1FBE63]',
      labelColor: 'text-[#008236]',
      pctColor: 'text-[#008236]',
      barBg: 'bg-[#D3F3CC]',
      barFill: 'bg-[#1FBE63]',
      icon: <CompletedIcon />,
      iconBg: 'bg-white/70',
    },
  ];

  return (
    <div
      className={cn('rounded-xl bg-white shadow-sm flex flex-col', className)}
      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)', padding: '16px 20px 16px 20px' }}
    >
      <h3 className="pb-3 mb-3 font-sans text-base font-bold text-[#1A1A1A] border-b border-[#F0F0F0]">
        {t('operationsOverview')}
      </h3>
      <div className="grid flex-1 grid-cols-2 auto-rows-fr" style={{ gap: '12px 16px' }}>
        {cards.map((card, i) => (
          <StatusCard key={i} {...card} />
        ))}
      </div>
    </div>
  );
}
